<template>
  <aside
    v-if="coachmark"
    class="tcm-card"
    role="region"
    :aria-labelledby="titleId"
    :aria-label="coachmark.title"
  >
    <div class="tcm-inner">
      <h2 :id="titleId" class="tcm-title">{{ coachmark.title }}</h2>
      <p class="tcm-body">{{ coachmark.body }}</p>
      <p v-if="coachmark.nextAction" class="tcm-next">{{ coachmark.nextAction }}</p>
      <div class="tcm-actions">
        <button
          v-if="coachmark.primaryTo"
          type="button"
          class="tcm-btn tcm-btn--primary"
          @click="onPrimaryNav"
        >
          {{ coachmark.primaryLabel }}
        </button>
        <button
          v-else
          type="button"
          class="tcm-btn tcm-btn--primary"
          @click="onDismiss"
        >
          {{ coachmark.primaryLabel || 'Got it' }}
        </button>
        <button
          v-if="coachmark.secondaryLabel && coachmark.secondaryTo"
          type="button"
          class="tcm-btn tcm-btn--ghost"
          @click="onSecondary"
        >
          {{ coachmark.secondaryLabel }}
        </button>
        <button
          v-else-if="coachmark.secondaryLabel && !coachmark.secondaryTo"
          type="button"
          class="tcm-btn tcm-btn--ghost"
          @click="onDismiss"
        >
          {{ coachmark.secondaryLabel }}
        </button>
        <button
          v-if="coachmark.showOpenChecklist"
          type="button"
          class="tcm-btn tcm-btn--ghost"
          @click="onOpenChecklist"
        >
          Open checklist
        </button>
        <button
          v-if="coachmark.primaryTo"
          type="button"
          class="tcm-btn tcm-btn--ghost"
          @click="onDismiss"
        >
          Dismiss
        </button>
      </div>
    </div>
  </aside>
</template>

<script>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useTutorialProgress } from '@/composables/useTutorialProgress';

export default {
  name: 'TutorialCoachmark',
  setup() {
    const tutorial = useTutorialProgress();
    const router = useRouter();
    const titleId = 'tutorial-coachmark-title';

    const coachmark = computed(() => tutorial.getActiveCoachmark());

    function onDismiss() {
      tutorial.dismissCoachmark();
    }

    function onPrimaryNav() {
      const cm = coachmark.value;
      tutorial.dismissCoachmark();
      if (cm?.primaryTo) {
        router.push(cm.primaryTo).catch(() => {});
      }
    }

    function onSecondary() {
      const cm = coachmark.value;
      const to = cm?.secondaryTo;
      tutorial.dismissCoachmark();
      if (to) router.push(to).catch(() => {});
    }

    function onOpenChecklist() {
      tutorial.dismissCoachmark({ silent: true, persistDismiss: false });
      tutorial.openChecklist();
    }

    return {
      coachmark,
      titleId,
      onDismiss,
      onPrimaryNav,
      onSecondary,
      onOpenChecklist,
    };
  },
};
</script>

<style scoped>
.tcm-card {
  position: fixed;
  left: 12px;
  right: 12px;
  bottom: calc(72px + env(safe-area-inset-bottom, 0px));
  z-index: 3200;
  display: flex;
  justify-content: center;
  pointer-events: none;
  text-align: left;
  font-family: 'Titillium Web', sans-serif;
}

.tcm-inner {
  pointer-events: auto;
  width: 100%;
  max-width: 420px;
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 14px;
  padding: 16px 16px 14px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.35);
  border: 1px solid rgba(148, 163, 184, 0.25);
  box-sizing: border-box;
}

.tcm-title {
  margin: 0 0 8px;
  font-size: 1.05rem;
  font-weight: 900;
  color: #ffffff;
}

.tcm-body {
  margin: 0 0 10px;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #cbd5e1;
}

.tcm-next {
  margin: 0 0 14px;
  font-size: 0.88rem;
  font-weight: 800;
  line-height: 1.4;
  color: #93c5fd;
}

.tcm-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tcm-btn {
  min-height: 44px;
  padding: 8px 14px;
  border-radius: 10px;
  font-family: inherit;
  font-size: 0.88rem;
  font-weight: 800;
  cursor: pointer;
}

.tcm-btn--primary {
  background: #0b7ee3;
  color: #fff;
  border: none;
}

.tcm-btn--ghost {
  background: transparent;
  color: #e2e8f0;
  border: 1px solid rgba(226, 232, 240, 0.35);
}

.tcm-btn:focus-visible {
  outline: 3px solid rgba(96, 165, 250, 0.55);
  outline-offset: 2px;
}

@media (min-width: 914px) {
  .tcm-card {
    bottom: 24px;
    left: auto;
    right: 24px;
    justify-content: flex-end;
  }
}

@media (max-width: 374px) {
  .tcm-inner {
    padding: 14px 12px 12px;
  }

  .tcm-title {
    font-size: 1rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tcm-card,
  .tcm-inner {
    transition: none;
  }
}
</style>
